
import { Card } from "@/components/ui/card";
import { TestResponse } from "@/types/test-response";
import { HelpCircle } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface TestIntegrityScoreProps {
  testResponse: TestResponse;
  isLoading?: boolean;
  className?: string;
}

export function TestIntegrityScore({ testResponse, isLoading = false, className }: TestIntegrityScoreProps) {
  const events = testResponse.events || [];
  
  // Use the database integrity score if available, otherwise calculate from events
  const integrityScore = testResponse.integrity_score ?? (events.length === 0 ? 100 : 0);
  
  // Define integrity level and color based on score
  const getIntegrityLevel = (score: number) => {
    if (score >= 80) return { level: 'High Integrity', color: 'text-green-600 dark:text-green-400' };
    if (score >= 60) return { level: 'Medium Integrity', color: 'text-amber-600 dark:text-amber-400' };
    return { level: 'Low Integrity', color: 'text-red-600 dark:text-red-400' };
  };
  
  const { level, color } = getIntegrityLevel(integrityScore);
  
  // Create gradient color for progress bar
  const getProgressStyle = () => {
    if (integrityScore >= 80) return 'bg-green-500 dark:bg-green-600';
    if (integrityScore >= 60) return 'bg-amber-500 dark:bg-amber-600';
    return 'bg-red-500 dark:bg-red-600';
  };

  return (
    <div className={cn("flex flex-col items-center", className)}>
      <div className="text-center mb-4">
        <p className="text-3xl font-bold mb-1">{integrityScore}%</p>
        <p className={`text-sm font-medium ${color}`}>{level}</p>
      </div>
      
      <Progress 
        value={integrityScore} 
        className={`h-2.5 mb-4 rounded-full ${getProgressStyle()}`}
      />
      
      <div className="flex justify-center">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/30 px-3 py-1.5 rounded-full">
                <span>
                  {events.length === 0 ? 
                    'This test was likely taken with high integrity.' : 
                    integrityScore >= 80 ? 
                    'High integrity test-taking behavior.' : 
                    integrityScore >= 60 ? 
                    'Some suspicious patterns detected.' : 
                    'Multiple integrity issues detected.'}
                </span>
                <HelpCircle className="h-3.5 w-3.5 text-muted-foreground" />
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs bg-slate-950 text-white dark:bg-white dark:text-slate-950">
              <p>Integrity score is calculated based on detected behaviors during test-taking. Higher scores indicate more natural test-taking behavior.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
}
